import { AlertTriangle, ChevronRight, CircleX, Info } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";
import { formatClock, formatDuration, formatMoney, formatRate, parseMicros } from "../lib/format";
import { usePrefs } from "./prefs";

/** `status.estimate` from a dry-run (resources.md §2.4, ADR-031). */
export interface EstimateView {
  costUSD?: { p50?: string; p90?: string };
  overheadUSD?: { p50?: string; p90?: string };
  rateUSDPerHour?: string;
  startup?: { cold?: { p50?: string; p90?: string }; warm?: { p50?: string; p90?: string } };
  holdUSD?: string;
  minimumChargeUSD?: string;
  validUntil?: string;
  placementPreview?: {
    offering?: string;
    regionClass?: string;
    interruptible?: boolean;
    explain?: { profile?: string; fallbacks?: string[]; rejected?: { class: string; reason: string }[] };
  };
  warnings?: { code: string; message: string }[];
  blockingReasons?: { code: string; message: string; fix?: string }[];
}

function goDuration(d: string | undefined): string | undefined {
  if (!d) return undefined;
  const m = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+(?:\.\d+)?)s)?$/.exec(d);
  if (!m) return d;
  const ms = ((Number(m[1] ?? 0) * 60 + Number(m[2] ?? 0)) * 60 + Number(m[3] ?? 0)) * 1000;
  return formatDuration(ms);
}

/** A money line shows only a positive amount: a zero overhead or minimum would read as free. */
const positive = (v: string | undefined) => (parseMicros(v) ?? 0) > 0;

/** An API message or fix ("add a card, or run `nodus billing portal`") as a sentence, with commands in code. */
function Sentence({ text }: { text: string }) {
  const s = text ? text[0]!.toUpperCase() + text.slice(1) + (/[.!?]$/.test(text) ? "" : ".") : text;
  return <>{s.split(/`([^`]+)`/).map((part, i) => (i % 2 ? <code key={i} className="font-mono">{part}</code> : part))}</>;
}

/** The NoCapacity warning in plain words; work held by a blocker cannot be launched until it is fixed. */
const waitsText = (blocked: boolean) =>
  blocked
    ? "Once that is fixed, the job can wait for capacity and start when it opens up, and Nodus charges nothing while it waits."
    : "You can still launch: the job waits and starts as soon as capacity opens up, and Nodus charges nothing while it waits.";

function Summary({ children }: { children: React.ReactNode }) {
  return (
    <summary className="flex w-fit cursor-pointer list-none items-center gap-1 py-1 text-small text-fg-muted select-none hover:text-fg-primary [&::-webkit-details-marker]:hidden">
      <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" aria-hidden />
      {children}
    </summary>
  );
}

function Line({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1">
      <dt className="text-ui text-fg-muted" title={hint}>
        {label}
      </dt>
      <dd className="text-ui text-fg-body tabular-nums">{value}</dd>
    </div>
  );
}

/**
 * The dry-run estimate (ui-spec §3.5 item 3): expected cost p50-p90 and the rate up front; the starting and stopping
 * overhead, start time, first hold, minimum charge and where this will run (offering names only, ADR-011) behind one
 * Details disclosure; blockers with their fix always shown. A stale estimate greys out until the next dry-run returns.
 */
export function EstimateBlock({
  estimate,
  loading = false,
  stale = false,
  error,
  rateTolerancePercent,
}: {
  estimate?: EstimateView;
  loading?: boolean;
  stale?: boolean;
  error?: string;
  /** Allowed increase between review and acquisition; the acquired rate is frozen. */
  rateTolerancePercent?: number;
}) {
  const { timeZone } = usePrefs();
  const e = estimate;
  const range = e?.costUSD?.p50 ? `${formatMoney(e.costUSD.p50)}–${formatMoney(e.costUSD.p90 ?? e.costUSD.p50)}` : undefined;
  const cold = e?.startup?.cold;
  const hasBreakdown = Boolean(positive(e?.overheadUSD?.p50) || cold?.p50 || positive(e?.holdUSD) || positive(e?.minimumChargeUSD));
  return (
    <section aria-label="Estimate" aria-busy={loading} className={cn("border-t border-line-subtle pt-3", (stale || loading) && "opacity-60")}>
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-ui font-medium text-fg-primary">Estimate</h3>
        <span className="text-small text-fg-muted" aria-live="polite">
          {loading ? "Estimating…" : stale ? "Out of date" : e?.validUntil ? `Estimate valid until ${formatClock(e.validUntil, timeZone)}` : ""}
        </span>
      </div>
      {error ? <p role="alert" className="text-ui text-danger-fg">{error}</p> : null}
      {e ? (
        <>
          <dl>
            {range ? <Line label="Expected cost" value={range} hint="p50–p90 to completion, including starting and stopping" /> : null}
            {e.rateUSDPerHour ? <Line label="Rate" value={formatRate(e.rateUSDPerHour)} /> : null}
          </dl>
          {e.blockingReasons?.map((b) => (
            <div key={b.code} role="alert" className="mt-3 flex gap-2 rounded-md border border-danger-border bg-danger-bg px-3 py-2 text-ui text-danger-fg">
              <CircleX className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>
                <Sentence text={b.message} />
                {b.fix ? <span className="block text-fg-body"><Sentence text={b.fix} /></span> : null}
              </span>
            </div>
          ))}
          {e.warnings?.filter((w) => w.code !== "RateIsCeiling").map((w) =>
            w.code === "NoCapacity" ? (
              <p key={w.code} className="mt-3 flex gap-2 text-small text-info-fg">
                <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                <span><Sentence text={waitsText(Boolean(e.blockingReasons?.length))} /></span>
              </p>
            ) : (
              <p key={w.code} className="mt-3 flex gap-2 text-small text-warning-fg">
                <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
                <span><Sentence text={w.message} /></span>
              </p>
            ),
          )}
          {hasBreakdown || e.placementPreview || rateTolerancePercent !== undefined ? (
            <details className="group text-ui">
              <Summary>Details</Summary>
              {rateTolerancePercent !== undefined && e.rateUSDPerHour ? <div className="mb-2">
                <dl><Line label="Launch rate limit" value={`Estimate + ${rateTolerancePercent}%`} /></dl>
                <p className="text-small text-fg-muted">The final rate may rise by up to {rateTolerancePercent}% before starting. Your rate is fixed once compute starts.</p>
              </div> : null}
              {hasBreakdown ? (
                <dl>
                  {positive(e.overheadUSD?.p50) ? <Line label="Starting and stopping" value={`≈ ${formatMoney(e.overheadUSD!.p50)}`} hint="The Boot and Teardown segments" /> : null}
                  {cold?.p50 ? <Line label="Starts in" value={`${goDuration(cold.p50)}–${goDuration(cold.p90 ?? cold.p50)}`} /> : null}
                  {positive(e.holdUSD) ? <Line label="First hold" value={formatMoney(e.holdUSD)} hint="Reserved from your balance, released at the end" /> : null}
                  {positive(e.minimumChargeUSD) ? <Line label="Minimum charge" value={formatMoney(e.minimumChargeUSD)} /> : null}
                </dl>
              ) : null}
              {e.placementPreview ? (
                <dl aria-label="Where this will run" className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-small">
                  <dt className="text-fg-muted">Offering</dt>
                  <dd className="font-mono text-fg-secondary">{e.placementPreview.offering ?? "—"}</dd>
                  <dt className="text-fg-muted">Region class</dt>
                  <dd className="text-fg-secondary">{e.placementPreview.regionClass ?? "—"}</dd>
                  {e.placementPreview.explain?.profile ? (
                    <>
                      <dt className="text-fg-muted">Profile</dt>
                      <dd className="text-fg-secondary">{e.placementPreview.explain.profile}</dd>
                    </>
                  ) : null}
                  {e.placementPreview.explain?.fallbacks?.length ? (
                    <>
                      <dt className="text-fg-muted">Fallbacks</dt>
                      <dd className="font-mono text-fg-secondary">{e.placementPreview.explain.fallbacks.join(", ")}</dd>
                    </>
                  ) : null}
                  {e.placementPreview.explain?.rejected?.map((r) => (
                    <React.Fragment key={r.class}>
                      <dt className="text-fg-muted">Not {r.class}</dt>
                      <dd className="text-fg-secondary">{r.reason}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              ) : null}
            </details>
          ) : null}
        </>
      ) : !loading && !error ? (
        <p className="text-ui text-fg-muted">—</p>
      ) : null}
    </section>
  );
}
